import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { NAV } from "@/data/constants";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") { navigate("/"); setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300); }
      else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else navigate(to);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-obsidian/90 backdrop-blur-xl border-b border-gold/15" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 lg:px-10">
        <button onClick={() => go("/")} data-testid="nav-logo" className="shrink-0 transition-transform duration-300 ease-out hover:scale-110">
          <LogoMark emblemSize={scrolled ? 44 : 50} wordmarkClass={scrolled ? "h-8" : "h-10"} />
        </button>

        <div className="hidden items-center gap-7 xl:flex">
          {NAV.map((n) => (
            <button
              key={n.label}
              onClick={() => go(n.to)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-sans text-[13px] tracking-wide text-softgrey transition-colors hover:text-warmwhite"
            >
              {n.label}
            </button>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <button
            onClick={() => go("/opportunities")}
            data-testid="nav-explore-cta"
            className="border border-gold/40 px-4 py-2 font-sans text-[12px] tracking-wide text-warmwhite transition-colors hover:bg-gold/10"
          >
            Explore Opportunities
          </button>
          <button
            onClick={() => go("/bespoke-sourcing")}
            data-testid="nav-submit-cta"
            className="group flex items-center gap-1.5 bg-gold px-4 py-2 font-sans text-[12px] font-medium tracking-wide text-obsidian transition-all hover:brightness-110"
          >
            Submit Your Requirements <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <button onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" className="text-warmwhite lg:hidden">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-gold/15 bg-obsidian/98 px-6 py-6 lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-4">
            {NAV.map((n) => (
              <button key={n.label} onClick={() => go(n.to)} className="text-left font-sans text-sm text-softgrey">
                {n.label}
              </button>
            ))}
            <button onClick={() => go("/opportunities")} className="mt-2 border border-gold/40 px-4 py-3 text-center font-sans text-sm text-warmwhite">
              Explore Opportunities
            </button>
            <button onClick={() => go("/bespoke-sourcing")} className="bg-gold px-4 py-3 text-center font-sans text-sm font-medium text-obsidian">
              Submit Your Requirements
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
